/**
 * The one place a form card name turns into a rendered card, plus the Forms
 * picker that selects between them.
 *
 * Extracted from `PlanningSection` so Initial Response can offer the same forms
 * without the Planning P phase stepper, agenda and Advance OP around them. Both
 * surfaces render identical cards because both come through here.
 */

import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  useComboboxAnchor,
} from '@/components/ui/combobox';

import { isPremiumResourceWidget } from '../premiumResourceManagement';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';

import { PlanningPageType } from './PlanningPage';
import { PlanningAgenda } from './PlanningAgenda';
import { ICS_201_WIDGET_NAME, type CardOption } from './cardOptions';
import { PLANNING_PHASES, WIDGET_TITLES } from './USCGPage';
import {
  useCurrentPeriod,
  type OperationalPeriod,
} from '../hooks/incidentHooks';
import { useICSContext } from './ICSContext';

import IncidentObjectives from './widgets/IncidentObjectives';
import OpenActionsTracker from './widgets/OpenActionsTracker';
import ICSFormsExport from './widgets/ICSFormsExport';
import PlanningObjectives from './widgets/PlanningObjectives';
import OrganizationAssignmentList from './widgets/OrganizationAssignmentList';
import SafetyPlan from './widgets/SafetyPlan/SafetyPlan';
import IncidentStatusSummary from './widgets/IncidentStatusSummary/IncidentStatusSummary';
import AssignmentList from './widgets/AssignmentList/AssignmentList';
import IncidentRadioCommunicationsPlan from './widgets/IncidentRadioCommunicationsPlan';
import MedicalPlan from './widgets/MedicalPlan/MedicalPlan';
import IAPSafetyAnalysis from './widgets/IAPSafetyAnalysis';
import IncidentActionPlan from './widgets/IncidentActionPlan';
import UnifiedWorkView from './UnifiedWorkView';
import Ics234StandaloneMatrix from './widgets/Ics234Table/Ics234StandaloneMatrix';
import Ics215StandaloneWorksheet from './widgets/Ics215Table/Ics215StandaloneWorksheet';
import Ics205aStandaloneForm from './widgets/Ics205aTable/Ics205aStandaloneForm';
import HazardousMaterials from './widgets/HazardousMaterials/HazardousMaterials';
import InitialResponseBriefing from '../initialResponsePage/InitialResponseBriefing';
import Ics201Card from './widgets/Ics201Card/Ics201Card';
import Ics217ACard from './widgets/NewCgForms/Ics217ACard';
import Ics220Card from './widgets/NewCgForms/Ics220Card';
import Ics220UasCard from './widgets/NewCgForms/Ics220UasCard';
import Ics225Card from './widgets/NewCgForms/Ics225Card';
import Ics232Card from './widgets/NewCgForms/Ics232Card';
import Ics232ACard from './widgets/NewCgForms/Ics232ACard';
import Ics235Card from './widgets/NewCgForms/Ics235Card';
import Ics236Card from './widgets/NewCgForms/Ics236Card';
import Ics261Card from './widgets/NewCgForms/Ics261Card';
import IcsCg5136Card from './widgets/NewCgForms/IcsCg5136Card';
import {
  Ics213RrDocumentCard,
  Ics214DocumentCard,
} from './widgets/DocumentCards/EntityDocumentCards';
import { Ics221List } from './widgets/DocumentCards/Ics221List';
import {
  Ics207DocumentCard,
  Ics211ADocumentCard,
  Ics211DocumentCard,
  Ics230DocumentCard,
  Ics238DocumentCard,
} from './widgets/DocumentCards/IncidentDocumentCards';

/** The hub's Assets view and the incident roster, loaded when a seat opens them. */
const IncidentAssets = React.lazy(
  () => import('components/MapPage/CorporateViewTabs/Resources')
);
const IncidentRoster = React.lazy(
  () => import('components/IncidentConfig/IncidentRosterData')
);

export {
  ICS_201_CARD_OPTION,
  ICS_201_WIDGET_NAME,
  type CardOption,
} from './cardOptions';

export type WidgetRenderContext = {
  planningPhases: PlanningPageType[];
  currentPhase: number;
  currentPeriod: ReturnType<typeof useCurrentPeriod>;
  /**
   * The period the cards author against. Read rather than derived, because it
   * is not always current + 1: during Initial Response it is 0.
   */
  workingPeriodNumber: number;
  /**
   * The period the cards render against. Planning P authors the working one;
   * Approved ICS Forms browses a past one.
   */
  renderPeriodNumber: number;
  /**
   * The period row, for the cards that need its dates rather than just its
   * number.
   */
  renderPeriod?: OperationalPeriod;
  /** Approved ICS Forms renders every card read-only. */
  readOnly: boolean;
  /**
   * Whether the package being rendered is the not-yet-started one Planning P is
   * assembling.
   */
  isFuturePackage: boolean;
};

/**
 * Assembles the render context from the hooks, for callers rendering inside the
 * incident workspace.
 */
export const useWidgetRenderContext = (
  planningPhases: PlanningPageType[],
  currentPhase: number
): WidgetRenderContext => {
  const currentPeriod = useCurrentPeriod();
  const { workingPeriodNumber } = useICSContext();
  return {
    planningPhases,
    currentPhase,
    currentPeriod,
    workingPeriodNumber,
    renderPeriodNumber: workingPeriodNumber,
    renderPeriod: currentPeriod.currentPeriod,
    readOnly: false,
    isFuturePackage: true,
  };
};

/**
 * Render context for Approved ICS Forms, which browses a period that has
 * already run.
 *
 * Same cards as Planning P, pinned to the selected period and read-only
 * throughout. Most cards take both from `ICSContext`, which the page has
 * already set to the selected period; this covers the few that are passed their
 * period and editability directly.
 */
export const useApprovedWidgetRenderContext = (
  renderPeriod: OperationalPeriod | undefined,
  renderPeriodNumber: number
): WidgetRenderContext => {
  const currentPeriod = useCurrentPeriod();
  const { workingPeriodNumber } = useICSContext();
  return {
    planningPhases: PLANNING_PHASES,
    currentPhase: 0,
    currentPeriod,
    workingPeriodNumber,
    renderPeriodNumber,
    renderPeriod,
    readOnly: true,
    isFuturePackage: false,
  };
};

export const renderWidget = (
  widgetName: string,
  ctx: WidgetRenderContext
): React.ReactNode => {
  const {
    planningPhases,
    currentPhase,
    renderPeriod,
    renderPeriodNumber,
    readOnly,
    isFuturePackage,
  } = ctx;

  switch (widgetName) {
    case 'agenda':
      return (
        <PlanningAgenda
          planningPhases={planningPhases}
          currentPhase={currentPhase}
        />
      );
    case ICS_201_WIDGET_NAME:
      return <Ics201Card />;
    case 'incidentObjectives':
      return <IncidentObjectives />;
    case 'incidentActionPlan':
      return (
        <IncidentActionPlan
          currPeriod={renderPeriod}
          isFuture={isFuturePackage}
        />
      );
    case 'workAnalysis':
      return <UnifiedWorkView />;
    case 'workAnalysisMatrix':
      return <Ics234StandaloneMatrix />;
    case 'operationalPlanningWorksheet':
      return <Ics215StandaloneWorksheet />;
    case 'communicationsList205a':
      return <Ics205aStandaloneForm />;
    case 'icsFormExport':
      return <ICSFormsExport />;
    case 'openActionsTracker':
      return <OpenActionsTracker />;
    case 'iapSafetyAnalysis':
      return <IAPSafetyAnalysis />;
    case 'initialResponse':
      return <InitialResponseBriefing />;
    case 'objectives':
      return <PlanningObjectives />;
    case 'organizationAssignmentList':
      return <OrganizationAssignmentList />;
    case 'safetyPlan':
      return <SafetyPlan />;
    case 'assignmentList':
      return (
        <AssignmentList periodNumber={renderPeriodNumber} readOnly={readOnly} />
      );
    case 'incidentRadioCommunicationsPlan':
      return <IncidentRadioCommunicationsPlan />;
    case 'medicalPlan':
      return <MedicalPlan />;
    case 'incidentStatusSummary':
      return (
        <IncidentStatusSummary currPeriod={renderPeriod} readOnly={readOnly} />
      );
    case 'hazardousMaterials':
      return <HazardousMaterials />;
    case 'commsResourceAvailability':
      return <Ics217ACard />;
    case 'airOperationsSummary':
      return <Ics220Card />;
    case 'uasAirOperationsSummary':
      return <Ics220UasCard />;
    case 'personnelPerformanceRating':
      return <Ics225Card />;
    case 'resourcesAtRisk':
      return <Ics232Card />;
    case 'acpSiteIndex':
      return <Ics232ACard />;
    case 'facilityNeedsAssessment':
      return <Ics235Card />;
    case 'tentativeReleaseList':
      return <Ics236Card />;
    case 'accountableResourceTracking':
      return <Ics261Card />;
    case 'cg5136':
      return <IcsCg5136Card formId="CG_5136" />;
    case 'cg5136x':
      return <IcsCg5136Card formId="CG_5136X" />;
    case 'incidentOrganizationChart':
      return <Ics207DocumentCard />;
    case 'checkInList':
      return <Ics211DocumentCard />;
    case 'dailyPersonnelSignIn':
      return <Ics211ADocumentCard />;
    case 'resourceRequestForm':
      return <Ics213RrDocumentCard />;
    case 'activityLogDocument':
      return <Ics214DocumentCard />;
    case 'demobilizationCheckOut':
      return <Ics221List />;
    case 'demobTrackingTable':
      return <Ics238DocumentCard />;
    case 'dailyMeetingSchedule':
      return <Ics230DocumentCard />;
    case 'incidentAssets':
      return (
        <React.Suspense fallback={null}>
          <div className="dark tw:text-foreground">
            <IncidentAssets />
          </div>
        </React.Suspense>
      );
    case 'incidentRoster':
      return (
        <React.Suspense fallback={null}>
          <IncidentRoster />
        </React.Suspense>
      );
    default:
      return null;
  }
};

/**
 * Same as `renderWidget`, but drops the 211 / 238 / 221 / 213RR cards when
 * Premium Resource Management is off.
 */
export function RenderWidget({
  widgetName,
  ctx,
}: {
  widgetName: string;
  ctx: WidgetRenderContext;
}): React.ReactElement | null {
  const premium = usePremiumResourceManagementFlag();
  if (!premium && isPremiumResourceWidget(widgetName)) return null;
  return <>{renderWidget(widgetName, ctx)}</>;
}

/**
 * Selectable forms: the given order first, then everything else in
 * `WIDGET_TITLES`, minus the exclusions.
 *
 * `extra` is appended ahead of the rest so Initial Response can lead with the
 * ICS-201.
 */
export const buildFormsDropdownOptions = ({
  widgetOrder = [],
  exclude = [],
  extra = [],
}: {
  widgetOrder?: string[];
  exclude?: string[];
  extra?: CardOption[];
}): CardOption[] => {
  const seen = new Set<string>();
  const options: CardOption[] = [];

  for (const option of extra) {
    if (seen.has(option.value)) continue;
    seen.add(option.value);
    options.push(option);
  }

  for (const widgetName of widgetOrder) {
    if (seen.has(widgetName) || exclude.includes(widgetName)) continue;
    seen.add(widgetName);
    const titleObj = WIDGET_TITLES.find((w) => w.widgetName === widgetName);
    options.push({
      label: titleObj ? titleObj.title : widgetName,
      value: widgetName,
    });
  }

  for (const { widgetName, title } of WIDGET_TITLES) {
    if (exclude.includes(widgetName) || seen.has(widgetName)) continue;
    seen.add(widgetName);
    options.push({ label: title, value: widgetName });
  }

  return options;
};

export const FormsDropdown: React.FC<{
  options: CardOption[];
  value: string;
  onChange: (value: string) => void;
  id?: string;
}> = ({ options, value, onChange, id = 'planning-forms-dropdown' }) => {
  const selected = options.find((opt) => opt.value === value) ?? null;
  const anchor = useComboboxAnchor();

  return (
    <div
      className="dark tw:inline-block tw:text-foreground"
      data-tour="planning-forms-dropdown"
    >
      <Combobox
        items={options}
        value={selected}
        onValueChange={(next) => {
          const option = next as CardOption | null;
          if (option) onChange(option.value);
        }}
        itemToStringValue={(item) => (item as CardOption).label}
      >
        <div ref={anchor}>
          <ComboboxTrigger
            id={id}
            render={<Button type="button" variant="outline" size="sm" />}
          >
            {selected ? `Forms - ${selected.label}` : 'Forms'}
          </ComboboxTrigger>
        </div>
        {/*
          The popup otherwise takes the trigger's width, which is far narrower
          than a form title like "Incident Action Plan Safety Analysis
          ICS-215A". Rows are a fixed height, so a title that wraps overlaps
          the one below it instead of reading as two lines.
        */}
        <ComboboxContent
          anchor={anchor}
          className="dark tw:w-96 tw:min-w-96 tw:data-[chips=true]:min-w-96"
        >
          <ComboboxInput placeholder="Search forms..." showTrigger={false} />
          <ComboboxEmpty>No matches</ComboboxEmpty>
          <ComboboxList>
            {(item: CardOption) => (
              <ComboboxItem
                key={item.value}
                value={item}
                className="tw:whitespace-nowrap"
              >
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
